import { i18n } from '@/commons/utils';
import { init as initSecretQuestion } from './authentication-secret-question';
import { getParameters, getURLParameters, getURLParameter, getHashParameters, resetForm, setStoredIdentifier, getStoredIdentifier } from '../customer-utils';

const URL = {
  REINITIALIZE: 'rest/model/atg/userprofiling/ProfileActor/reinitialize',
  BACK: 'rest/model/atg/userprofiling/ProfileActor/backToPreviousState',
  RESEND_CODE: 'rest/model/atg/userprofiling/ProfileActor/forgottenPassword',
};

const STATES = {
  CHECK_CREDENTIALS: 0,
  CHOOSE_ECODE_PCODE: 2,
  CHOOSE_PCODE_ECODE: 3,
  EMAIL_CODE_SENT: 4,
  CHOOSE_ELINK_ECODE: 5,
  SECRET_QUESTION: 6,
  CHECK_CODE: 7,
  PREFILLED_FORM: 8,
  RESET_PSW: 9,
  RESET_PSW_SUCCESS: 10,
  EMAIL_LINK_SENT_ECODE: 11,
  GENERIC_MESSAGE: 12,
  EMAIL_LINK_SENT_PCODE: 13,
  EMAIL_LINK_SENT: 14,
  NOT_EXISTS: 15
};

const TYPES = {
  EMAIL_CODE: 'CMAIL',
  MOBILE_CODE: 'CPHONE',
  EMAIL_LINK: 'LMAIL',
};


const setRules = form => {
  $(form).validate(Object.assign(
    $.validator.cubeOptions,
    {
      rules: {
        login: {
          maxlength: 100,
          mobile: {
            depends: element => element.classList.contains('login-mobile')
          },
          email: {
            depends: element => element.classList.contains('login-email')
          },
          emailmobile: {
            depends: element => element.classList.contains('login-both')
          }
        },
        actualPassword: {
          minlength: 6
        },
        password: {
          passwordCheck: true
        },
        confirmPassword: {
          equalTo: '#password'
        },
        messages: {
          text: { required: i18n('account.lostpassword.questionOrAnswerEmpty') },
          actualPassword: {
            minlength: i18n('profile.updatePassword.newpassword.minLength'),
            required: i18n('profile.updatePassword.actualPassword.required')
          },
          password: {
            required: i18n('profile.updatePassword.newpassword.required'),
            minlength: i18n('profile.updatePassword.newpassword.minLength')
          },
          confirmPassword: {
            required: i18n('profile.updatePassword.confirmPassword.required'),
            minlength: i18n('profile.updatePassword.newpassword.minLength'),
            equalTo: i18n('profile.updatePassword.confirmPassword.equalsTo')
          }
        }
      },
      ignore: ':hidden:not(.selectpicker)',
      unhighlight: (element, error, valid) => {
        const parent = element.closest('.form-group');
        const { classList } = parent;

        classList.remove(error);

        if(element.value.length && valid)
          classList.add(valid);
      }
    }
  ));
};


/**
 * Display an error if the XHR returns error(s).
 *
 * @param {Json} response The XHR response.
 * @param {HTMLElement} button The form submit button.
 * @returns {Boolean}
 */
const ajaxError = (response, button) => {
  if (
    typeof response === 'string' ||
    typeof response === 'object' && response.responseTO && response.responseTO.errors[0] && response.responseTO.errors[0].message
  ) {
    if (typeof response === 'string') {
      cube.alert.error(i18n('error.oc.call'));
    }
    else {
      const { code, message } = response.responseTO.errors[0];
      const validator = $(button.closest('form')).validate();

      if (code === 'account.authentication.forbiddenPassword')
        validator.showErrors({
          'password': message
        });

      else if (code === 'account.authentication.wrongPassword')
        validator.showErrors({
          'actualPassword': message
        });

      else
        cube.alert.error(message);
    }

    if (button)
      button.disabled = false;

    return true;
  }

  return false;
};


/**
 * Display a message based on the selected channel to warn the user that a code has been sent to him.
 *
 * @param {String} option The selected channel.
 * @param {Json} data The XHR response.
 */
const showCodeMessage = (option, data) => {
  const countdown = $(cube.$id('resend-code')).data('countdown');

  if (option) {
    switch (option) {
      case TYPES.EMAIL_CODE:
        if (data.emailSent) {
          countdown.go();
          cube.alert.information(i18n('account.lostpassword.codeSentToEmail', data.email));
        }
        break;

      case TYPES.MOBILE_CODE:
        if (data.smsSent) {
          countdown.go();
          cube.alert.information(i18n('account.lostpassword.codeSentToMobile', data.mobile));
        }
        break;

      case TYPES.EMAIL_LINK:
        if (data.emailSent)
          cube.alert.information(i18n('account.lostpassword.linkSentToEmail', data.email));
        break;

      default:
    }
  }
  else {
    countdown.go();

    if (data.email)
      cube.alert.information(i18n('account.lostpassword.codeSentToEmail', data.email));
    else if (data.mobile)
      cube.alert.information(i18n('account.lostpassword.codeSentToMobile', data.mobile));
  }
};


/**
 * Display one block and hide the others.
 *
 * @param {String} id The block id to display.
 */
const showBlock = id => {
  const blocks = [
    'identifier-form',
    'reset-password-method-form',
    'verification-code-form',
    'new-password-form',
    'secret-question-form',
    'mail-sent',
  ];

  blocks.forEach(block => {
    if ((Array.isArray(id) && id.includes(block)) || block === id)
      cube.$id(block).style.display = 'block';
    else
      cube.$id(block).style.display = 'none';
  });
};


/**
 * Display a recover password process depending on the server configuration.
 *
 * @param {Json} data The data in the XHR response.
 * @param {Json} params The form paratemers.
 */
const displayNextState = (data, params) => {
  const title = cube.$('#lost-password h2');
  const labels = cube.$$('#reset-password-method-form .indicator');
  const radios = cube.$$('#reset-password-method-form input');
  const select = cube.$id('question-id');
  const answer = cube.$id('secret-answer');
  const locale = cube.$id('locale');
  const format = cube.$id('date-of-birth-format');
  let selected = '';

  switch (data.nextState) {
    case STATES.CHECK_CREDENTIALS:
      title.textContent = i18n('account.lostpassword.title');
      showBlock('identifier-form');
      break;

    case STATES.CHOOSE_ECODE_PCODE:
      title.textContent = i18n('account.lostpassword.title');
      showBlock('reset-password-method-form');

      labels[0].innerHTML = `${ i18n('account.lostpassword.resetByMail') } <strong>${ data.email || '' }</strong>`;
      radios[0].value = TYPES.EMAIL_CODE;
      radios[0].dataset.email = data.email;
      radios[0].checked = true;

      labels[1].innerHTML = `${ i18n('account.lostpassword.resetByPhone') } <strong>${ data.mobile || '' }</strong>`;
      radios[1].dataset.mobile = data.mobile;
      radios[1].value = TYPES.MOBILE_CODE;
      break;

    case STATES.CHOOSE_PCODE_ECODE:
      title.textContent = i18n('account.lostpassword.title');

      showBlock('reset-password-method-form');
      labels[1].innerHTML = `${ i18n('account.lostpassword.resetByPhone') } <strong>${ data.mobile || '' }</strong>`;
      radios[1].value = TYPES.MOBILE_CODE;
      radios[1].dataset.email = data.mobile;
      radios[1].checked = true;

      labels[0].innerHTML = `${ i18n('account.lostpassword.resetByMail') } <strong>${ data.email || '' }</strong>`;
      radios[0].dataset.email = data.email;
      radios[0].value = TYPES.EMAIL_CODE;
      break;

    case STATES.EMAIL_CODE_SENT:
      title.textContent = i18n('account.lostpassword.confirmCodeTitle');
      showBlock('verification-code-form');

      selected = select.value;

      if (selected)
        showCodeMessage(selected, data);
      else
        showCodeMessage(TYPES.EMAIL_CODE, data);

      break;

    case STATES.CHOOSE_ELINK_ECODE:
      title.textContent = i18n('account.lostpassword.title');
      showBlock('reset-password-method-form');

      labels[0].innerHTML = `${ i18n('account.lostpassword.resetByMailByLink') } <strong>${ data.email || '' }</strong>`;
      radios[0].value = TYPES.EMAIL_LINK;
      radios[0].dataset.email = data.email;
      radios[0].checked = true;

      labels[1].innerHTML = `${ i18n('account.lostpassword.resetByMail') } <strong>${ data.email || '' }</strong>`;
      radios[1].dataset.email = data.email;
      radios[1].value = TYPES.EMAIL_CODE;
      break;

    case STATES.SECRET_QUESTION:
      showBlock('secret-question-form');
      initSecretQuestion(select, data.fields, answer, locale.value, format.value);
      break;

    case STATES.CHECK_CODE:
      title.textContent = i18n('account.lostpassword.confirmCodeTitle');
      showBlock('verification-code-form');

      if (data.previousState === STATES.SECRET_QUESTION) {
        selected = select.value;

        if (selected)
          showCodeMessage(select.value);
      }
      else {
        showCodeMessage(
          data.smsSent ? TYPES.MOBILE_CODE : TYPES.EMAIL_CODE,
          data
        );
      }

      break;

    case STATES.PREFILLED_FORM:
      window.location.href = data.url;
      break;

    case STATES.RESET_PSW:
      title.textContent = i18n('account.lostpassword.resetPasswordTitle');
      showBlock('new-password-form');
      break;

    case STATES.RESET_PSW_SUCCESS:
      showBlock(['verification-code-form', 'secret-question-form']);
      break;

    case STATES.GENERIC_MESSAGE:
      cube.ajax
        .post(URL.REINITIALIZE, params)
        .onSuccess(response => {
          if (ajaxError(response))
            return;

          showBlock('mail-sent');
        })
        .onError(response => ajaxError(response))
        .call();
      break;

    case STATES.NOT_EXISTS:
      cube.ajax
        .post(URL.REINITIALIZE, params)
        .onSuccess(response => {
          if (ajaxError(response))
            return;

          /* eslint-disable no-underscore-dangle */
          cube.alert.error(i18n(
            window.AccountUtils._isEmail(params.login)
              ? 'account.lostpassword.emailNotFound'
              : 'account.lostpassword.mobileNotFound'
          ));
          /* eslint-enable no-underscore-dangle */
        })
        .onError(response => ajaxError(response))
        .call();
      break;

    case STATES.EMAIL_LINK_SENT:
    case STATES.EMAIL_LINK_SENT_ECODE:
    case STATES.EMAIL_LINK_SENT_PCODE:
      showBlock('mail-sent');
      break;

    default:
  }
};


/**
 * Stop the sms code recover process and show previous step.
 *
 * @param {HTMLElement} button The back button.
 */
function back (button) {
  button.disabled = true;

  cube.ajax
    .post(URL.BACK)
    .onSuccess(response => {
      const resend = cube.$id('resend-code');

      $(resend).data('countdown').stop();
      button.disabled = false;

      displayNextState(response.responseTO.data);
    })
    .onError(response => ajaxError(response, button))
    .call();
}


/**
 * Reset the form and start the cancellation of the code validation process.
 *
 * @param {Event} e
 */
function cancelCodeValidation (e) {
  e.preventDefault();

  const form = this.closest('form');

  resetForm(form);
  back(this);
}


/**
 * Display a message if the XHR returns no error.
 *
 * @param {Json} response The XHR response.
 * @param {HTMLElement} link The resend code link.
 */
const resendCodeSuccess = (response, link) => {
  if (ajaxError(response))
    return;

  const { data } = response.responseTO;
  const option = cube.$('[name=optionsRadios]:checked');
  const countdown = $(link).data('countdown');

  countdown.go();

  showCodeMessage(option || TYPES.EMAIL_LINK, data);
};


/**
 * Call an API to resend a SMS validation code.
 *
 * @param {Event} e
 */
function resendCode (e) {
  e.preventDefault();

  const form = this.closest('form');
  const parameters = getParameters(form);

  cube.ajax
    .post(URL.RESEND_CODE, parameters)
    .onSuccess(response => resendCodeSuccess(response, this))
    .onError(response => ajaxError(response))
    .call();
}


/**
 * Display the next step if the customer exists.
 *
 * @param {Event} e
 */
const customerExistsSuccess = (response, parameters, button) => {
  if (ajaxError(response, button))
    return;

  displayNextState(response.responseTO.data, parameters);

  button.disabled = false;
};


/**
 * Redirect to home page when the customer's password is updated.
 *
 */
const passwordChangedSuccess = (response, button) => {
  if (ajaxError(response, button))
    return;

  button.disabled = false;
  cube.alert.success(i18n('profile.changesSaved'));

  window.setTimeout(() => {
    window.location.href = getURLParameter(window.location.search, 'redirectUrl') || '/';
  }, 2000);
};


/**
 * Display the next step if the verification code is correct.
 *
 * @param {Object} parameters The form parameters.
 * @param {HTMLElement} button The form button.
 */
const checkVerificationCodeSuccess = (response, parameters, button) => {
  if (ajaxError(response, button))
    return;

  button.disabled = false;

  const { valid } = response.responseTO.data;
  const validator = $(button.closest('form')).validate();

  if (!valid)
    validator.showErrors({'smsCode': i18n('validationMsg.invalidCode')});

  const URLParameters = window.location.search && getURLParameters(window.location.search);

  if (URLParameters.recoverData)
    setStoredIdentifier({smsCode: parameters.smsCode, ...getStoredIdentifier('MAIN')}, 'MAIN');

  displayNextState(response.responseTO.data, parameters);
};


/**
 * Handle the forms submission.
 *
 * @param {Event} e
 */
function sendForm (e) {
  e.preventDefault();

  const form = this;
  const button = form.$('button.cta-v2');

  if ($(form).valid()) {
    const callbacks = {
      'identifier-form': customerExistsSuccess,
      'reset-password-method-form': customerExistsSuccess,
      'verification-code-form': checkVerificationCodeSuccess,
      'new-password-form': passwordChangedSuccess,
      'secret-question-form': customerExistsSuccess
    };

    const { action } = this.dataset;
    const parameters = Object.assign(
      getURLParameters(window.location.search) || {},
      getParameters(form),
    );

    button.disabled = true;

    cube.ajax
      .post(action, parameters)
      .onSuccess(response => callbacks[form.id](response, parameters, button))
      .onError(response => ajaxError(response, button))
      .call();
  }
};


export const init = () => {
  const forms = cube.$$('#lost-password form');

  const hashParameters = getHashParameters(window.location.hash);
  if (hashParameters) {
    cube.$id('login').value = hashParameters.login;

    const countryCode = cube.$('[name="countryCode"]');
    if (countryCode)
      countryCode.value = hashParameters.countryCode;
  }

  forms.forEach(form => setRules(form));
  forms.on('submit', sendForm);

  cube.$id('cancel-code-validation').on('click', cancelCodeValidation);

  const resend = cube.$id('resend-code');
  $(resend).data('countdown', new window.decathlon.userAccount.Countdown($(resend), 30));
  resend.on('click', resendCode);
};



// WEBPACK FOOTER //
// ./src/customer/authentication/authentication-lost-password.js